import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Input,
  Select,
  Option,
  Table,
  Sheet,
  Checkbox,
  IconButton,
  Tooltip,
  Modal,
  ModalDialog,
  ModalClose,
} from '@mui/joy';
import {
  CheckCircle as CheckIcon,
  XCircle as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  MagnifyingGlass as SearchIcon,
  DownloadSimple as DownloadIcon,
  Eye as ViewIcon,
  ArrowCounterClockwise as RevertIcon,
  ArrowsLeftRight as CompareIcon,
  ArrowLeft as BackIcon,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface DeploymentRecord {
  id: string;
  environment: 'staging' | 'production';
  status: 'completed' | 'failed' | 'cancelled' | 'rolled_back';
  version: string;
  created_at: string;
  completed_at?: string;
  deployed_by: string;
  notes?: string;
  duration?: number;
  files_changed: number;
  url?: string;
}

interface FilterState {
  environment: 'all' | 'staging' | 'production';
  status: 'all' | 'completed' | 'failed' | 'cancelled';
  dateRange: 'all' | 'week' | 'month' | 'quarter';
}

export const DeploymentHistory: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeployments, setSelectedDeployments] = useState<string[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    environment: 'all',
    status: 'all',
    dateRange: 'all',
  });

  // Fetch deployment history
  const { data: deployments, isLoading } = useQuery<DeploymentRecord[]>({
    queryKey: ['deployment-history', siteId, filters, searchTerm],
    queryFn: async (): Promise<DeploymentRecord[]> => {
      const params = new URLSearchParams();
      if (filters.environment !== 'all') params.append('environment', filters.environment);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateRange !== 'all') params.append('date_range', filters.dateRange);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`/api/cms/sites/${siteId}/deployments/?${params}`);
      return ((response.data as any)?.results || response.data || []) as DeploymentRecord[];
    },
  });

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: async (deploymentId: string) => {
      const response = await api.post(`/api/cms/deployments/${deploymentId}/rollback/`, {});
      return response.data;
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'cancelled': return 'neutral';
      case 'rolled_back': return 'warning';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckIcon color="var(--joy-palette-success-500)" />;
      case 'failed': return <ErrorIcon color="var(--joy-palette-danger-500)" />;
      case 'cancelled': return <WarningIcon />;
      case 'rolled_back': return <RevertIcon color="var(--joy-palette-warning-500)" />;
      default: return <InfoIcon />;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '—';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRollback = async (deploymentId: string) => {
    if (window.confirm('Are you sure you want to rollback to this deployment?')) {
      await rollbackMutation.mutateAsync(deploymentId);
    }
  };

  const handleBulkAction = (action: string) => {
    // Handle bulk operations
    console.log(`Bulk action: ${action} on deployments:`, selectedDeployments);
  };

  const filteredDeployments = deployments?.filter(deployment => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        deployment.version.toLowerCase().includes(searchLower) ||
        deployment.deployed_by.toLowerCase().includes(searchLower) ||
        deployment.notes?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  }) || [];

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography level="h2">Deployment History</Typography>
        <Sheet sx={{ mt: 2, p: 3 }}>Loading deployment history...</Sheet>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startDecorator={<BackIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/deploy`)}
          >
            Back to Deploy
          </Button>
          <Box>
            <Typography level="h2">Deployment History</Typography>
            <Typography level="body-md" color="neutral">
              View and manage past deployments
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<DownloadIcon />}
          >
            Export
          </Button>
          {selectedDeployments.length > 0 && (
            <Button
              variant="outlined"
              color="danger"
              onClick={() => handleBulkAction('delete')}
            >
              Delete Selected ({selectedDeployments.length})
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters and Search */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12} sm={3}>
              <Input
                placeholder="Search deployments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                startDecorator={<SearchIcon />}
              />
            </Grid>
            <Grid xs={12} sm={2}>
              <Select
                value={filters.environment}
                onChange={(_, value) => setFilters({ ...filters, environment: value as any })}
              >
                <Option value="all">All Environments</Option>
                <Option value="staging">Staging</Option>
                <Option value="production">Production</Option>
              </Select>
            </Grid>
            <Grid xs={12} sm={2}>
              <Select
                value={filters.status}
                onChange={(_, value) => setFilters({ ...filters, status: value as any })}
              >
                <Option value="all">All Status</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Grid>
            <Grid xs={12} sm={2}>
              <Select
                value={filters.dateRange}
                onChange={(_, value) => setFilters({ ...filters, dateRange: value as any })}
              >
                <Option value="all">All Time</Option>
                <Option value="week">Last Week</Option>
                <Option value="month">Last Month</Option>
                <Option value="quarter">Last Quarter</Option>
              </Select>
            </Grid>
            <Grid xs={12} sm={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<CompareIcon />}
                  onClick={() => setShowCompareModal(true)}
                  disabled={selectedDeployments.length !== 2}
                >
                  Compare
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="primary">
            <CardContent>
              <Typography level="body-sm">Total Deployments</Typography>
              <Typography level="h3">{filteredDeployments.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="success">
            <CardContent>
              <Typography level="body-sm">Successful</Typography>
              <Typography level="h3">
                {filteredDeployments.filter(d => d.status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="danger">
            <CardContent>
              <Typography level="body-sm">Failed</Typography>
              <Typography level="h3">
                {filteredDeployments.filter(d => d.status === 'failed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="warning">
            <CardContent>
              <Typography level="body-sm">Avg Duration</Typography>
              <Typography level="h3">
                {filteredDeployments.length > 0
                  ? formatDuration(
                      filteredDeployments
                        .filter(d => d.duration)
                        .reduce((acc, d) => acc + (d.duration || 0), 0) /
                      filteredDeployments.filter(d => d.duration).length
                    )
                  : '—'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deployment Table */}
      <Card variant="outlined">
        <Sheet>
          <Table stickyHeader>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Checkbox
                    indeterminate={
                      selectedDeployments.length > 0 &&
                      selectedDeployments.length < filteredDeployments.length
                    }
                    checked={
                      filteredDeployments.length > 0 &&
                      selectedDeployments.length === filteredDeployments.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDeployments(filteredDeployments.map(d => d.id));
                      } else {
                        setSelectedDeployments([]);
                      }
                    }}
                  />
                </th>
                <th>Status</th>
                <th>Environment</th>
                <th>Version</th>
                <th>Deployed By</th>
                <th>Date</th>
                <th>Duration</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeployments.map((deployment) => (
                <tr key={deployment.id}>
                  <td>
                    <Checkbox
                      checked={selectedDeployments.includes(deployment.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDeployments([...selectedDeployments, deployment.id]);
                        } else {
                          setSelectedDeployments(
                            selectedDeployments.filter(id => id !== deployment.id)
                          );
                        }
                      }}
                    />
                  </td>
                  <td>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getStatusIcon(deployment.status)}
                      <Chip size="sm" color={getStatusColor(deployment.status)}>
                        {deployment.status}
                      </Chip>
                    </Stack>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={deployment.environment === 'production' ? 'danger' : 'primary'}
                    >
                      {deployment.environment}
                    </Chip>
                  </td>
                  <td>
                    <Typography level="body-sm">v{deployment.version}</Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">{deployment.deployed_by}</Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {new Date(deployment.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      {new Date(deployment.created_at).toLocaleTimeString()}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {formatDuration(deployment.duration)}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">{deployment.files_changed}</Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      {deployment.url && (
                        <Tooltip title="View deployment">
                          <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={() => window.open(deployment.url, '_blank')}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {deployment.status === 'completed' && (
                        <Tooltip title="Rollback to this version">
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleRollback(deployment.id)}
                          >
                            <RevertIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View details">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => navigate(`/cms/sites/${siteId}/deploy/progress/${deployment.id}`)}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      </Card>

      {/* Compare Modal */}
      <Modal open={showCompareModal} onClose={() => setShowCompareModal(false)}>
        <ModalDialog size="lg">
          <ModalClose />
          <Typography level="h4" mb={2}>Compare Deployments</Typography>
          <Typography level="body-sm">
            Comparison feature coming soon...
          </Typography>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
